import { useState, useEffect, useRef } from "react";

/*
    This is different from debounce in that it will trigger the first value update immediately,
    and then only update to the latest value every given delay milliseconds.
    A value update will never happen more frequently than the given delay.
    This is more ideal for real-time filtering, search-as-you-type, drag/resize operations, and position syncing.
    It gives a more responsive feel than debounce but will trigger more often.
*/
export function useThrottle<T>(value: T, delay: number): T {
  const [throttledValue, setThrottledValue] = useState<T>(value);
  // ref to store the latest incoming value
  const valueRef = useRef<T>(value);
  // timeout ref indicates a pending throttle interval
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  // flag to track if value has changed during throttle period
  const hasChangedRef = useRef<boolean>(false);

  // Always keep the latest value in the ref
  valueRef.current = value;

  // Handle throttling logic
  useEffect(() => {
    // If there's no active throttle period
    if (timeout.current === null) {
      // Update immediately on first change (leading edge)
      setThrottledValue(valueRef.current);

      const handleThrottleEnd = () => {
        // If value changed during throttle period, update and start a new period
        if (hasChangedRef.current) {
          setThrottledValue(valueRef.current);
          hasChangedRef.current = false;
          timeout.current = setTimeout(handleThrottleEnd, delay);
        } else {
          // No changes during period, end throttling
          timeout.current = null;
        }
      };
      // Start throttle period
      timeout.current = setTimeout(handleThrottleEnd, delay);
    } else {
      // We're in a throttle period, mark that value has changed
      hasChangedRef.current = true;
    }
  }, [value, delay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeout.current !== null) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
    };
  }, []);

  return throttledValue;
}
